import React from 'react';

import { Box, Paper, Typography } from '@mui/material';

import type { ManualApprovalState } from '../types';
import { formatPrice } from '../utils';
import type { DealOptionsInclusionsDealRow } from '../utils';

import InclusionRow, { INCLUSION_COLUMNS } from './InclusionRow';

interface Props {
  deal: DealOptionsInclusionsDealRow;
  manualApprovals: ManualApprovalState;
  showAutoApproved: boolean;
  onApprovalChange: (rowId: string) => void;
  onUnapprovalChange: (rowId: string) => void;
}

export default function DealCard({
  deal,
  manualApprovals,
  showAutoApproved,
  onApprovalChange,
  onUnapprovalChange,
}: Props) {
  const visibleInclusions = deal.inclusions.filter((inc) => {
    if (showAutoApproved) return true;
    return inc.status !== 'approved' && !manualApprovals[inc.id];
  });

  return (
    <Paper variant="outlined" sx={{ borderRadius: 1.25, overflow: 'hidden' }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', columnGap: 1.5, rowGap: 0.5 }}>
          <FieldLabel>DEAL NAME</FieldLabel>
          <FieldLabel>ROOM NAME</FieldLabel>
          <FieldLabel>SELL PRICE</FieldLabel>
          <FieldLabel>COST PRICE</FieldLabel>
          <FieldLabel>% MARGIN</FieldLabel>
          <FieldLabel>RACK RATE</FieldLabel>

          <FieldBox value={deal.dealOptionName || '—'} />
          <FieldBox value={deal.roomName || '—'} />
          <FieldBox value={formatPrice(deal.sellPrice, deal.currency)} />
          <FieldBox value={formatPrice(deal.costPrice, deal.currency)} />
          <FieldBox value={deal.marginPercent != null ? `${deal.marginPercent}%` : '—'} />
          <FieldBox value={formatPrice(deal.rackRate, deal.currency)} />
        </Box>
      </Box>

      {visibleInclusions.length === 0 ? (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {deal.inclusions.length === 0 ? 'No inclusions configured.' : 'All inclusions approved.'}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ width: 'max-content', minWidth: '100%' }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: INCLUSION_COLUMNS,
                gap: 1.5,
                bgcolor: 'grey.50',
                borderBottom: 1,
                borderColor: 'divider',
                px: 2,
                py: 1,
              }}
            >
              <FieldLabel>Inclusion Name</FieldLabel>
              <FieldLabel>Inclusion Description</FieldLabel>
              <FieldLabel>Type</FieldLabel>
              <FieldLabel>Deal Option Inclusion RRP</FieldLabel>
              <FieldLabel>Inclusion Asset Value</FieldLabel>
              <FieldLabel>Total Value</FieldLabel>
              <FieldLabel>RRP Comparison</FieldLabel>
              <FieldLabel>Action</FieldLabel>
            </Box>
            {visibleInclusions.map((inc) => (
              <InclusionRow
                key={inc.id}
                currency={deal.currency}
                inc={inc}
                manuallyApproved={!!manualApprovals[inc.id]}
                onApprovalChange={onApprovalChange}
                onUnapprovalChange={onUnapprovalChange}
              />
            ))}
          </Box>
        </Box>
      )}
    </Paper>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      fontWeight={600}
      sx={{ letterSpacing: 0.5, whiteSpace: 'nowrap' }}
    >
      {children}
    </Typography>
  );
}

function FieldBox({ value }: { value: string }) {
  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        fontSize: 13,
        overflow: 'hidden',
        px: 1.5,
        py: 0.75,
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {value}
    </Box>
  );
}
